import { Fragment } from 'react';
import styled from 'styled-components';
import { Link, Outlet, useLocation, useSearchParams } from 'react-router-dom';
import { ReadOnlyBanner, useAgentPermission } from '@entities/user';
import { MTO_MODULES, MTO_PHASE_TITLES, isMtoModuleId, mtoModule } from '@entities/mto';
import type { MtoModulePhase } from '@entities/mto';
import { riseIn } from '@shared/theme/animations';
import { MtoRequestsRailCount } from './MtoRequests';

/* Раскладка «рабочий стол»: постоянный левый рельс модулей + основная область.
   Рельс виден и на обзоре, и на странице модуля — это отличает Снабженца от
   Экономиста (тот сделан секциями сверху-вниз). Цвет — фирменный (терракота). */

const Shell = styled.div`
  max-width: 1320px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 232px minmax(0, 1fr);
  gap: ${({ theme }) => theme.spacing(7)};
  align-items: start;

  @media (max-width: 920px) {
    grid-template-columns: minmax(0, 1fr);
    gap: ${({ theme }) => theme.spacing(5)};
  }
`;

const Rail = styled.nav`
  position: sticky;
  top: ${({ theme }) => theme.spacing(4)};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(0.5)};

  /* На узких экранах рельс превращается в горизонтальную ленту над контентом. */
  @media (max-width: 920px) {
    position: static;
    flex-direction: row;
    flex-wrap: nowrap;
    overflow-x: auto;
    gap: ${({ theme }) => theme.spacing(2)};
    padding-bottom: ${({ theme }) => theme.spacing(2)};
    -webkit-overflow-scrolling: touch;
  }
`;

const Brand = styled(Link)`
  font-size: 17px;
  font-weight: 700;
  letter-spacing: -0.01em;
  color: ${({ theme }) => theme.colors.text.primary};
  text-decoration: none;
  margin-bottom: ${({ theme }) => theme.spacing(3)};
  padding: ${({ theme }) => `${theme.spacing(1)} ${theme.spacing(2)}`};

  & .accent {
    color: ${({ theme }) => theme.colors.primary};
    font-weight: 800;
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${({ theme }) => theme.shadow.focus};
    border-radius: ${({ theme }) => theme.radius.sm};
  }

  @media (max-width: 920px) {
    display: none;
  }
`;

const PhaseLabel = styled.div`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.muted};
  margin: ${({ theme }) => `${theme.spacing(4)} ${theme.spacing(2)} ${theme.spacing(1)}`};

  @media (max-width: 920px) {
    display: none;
  }
`;

const RailLink = styled(Link)<{ $active: boolean }>`
  display: flex;
  align-items: baseline;
  gap: ${({ theme }) => theme.spacing(2)};
  font-size: 13.5px;
  font-weight: 600;
  text-decoration: none;
  white-space: nowrap;
  padding: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(3)}`};
  border-radius: ${({ theme }) => theme.radius.md};
  border-left: 2px solid ${({ theme, $active }) => ($active ? theme.colors.primary : 'transparent')};
  background: ${({ theme, $active }) => ($active ? theme.colors.primarySoft : 'transparent')};
  color: ${({ theme, $active }) => ($active ? theme.colors.primary : theme.colors.text.secondary)};
  transition:
    background ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut},
    color ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut};

  &:hover {
    background: ${({ theme }) => theme.colors.surfaceHover};
    color: ${({ theme }) => theme.colors.text.primary};
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${({ theme }) => theme.shadow.focus};
  }

  @media (max-width: 920px) {
    border-left: none;
    border: 1px solid ${({ theme, $active }) => ($active ? theme.colors.primary : theme.colors.border)};
    border-radius: ${({ theme }) => theme.radius.pill};
  }
`;

const RailNum = styled.span`
  font-variant-numeric: tabular-nums;
  font-size: 11px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.muted};
  flex-shrink: 0;
`;

const Main = styled.div`
  min-width: 0;
`;

const Header = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing(6)};
  animation: ${riseIn} 420ms ${({ theme }) => theme.motion.easeOut} both;
`;

const Title = styled.h1`
  margin: 0;
  font-size: 26px;
  font-weight: 700;
  letter-spacing: -0.02em;

  & .accent {
    color: ${({ theme }) => theme.colors.primary};
    font-weight: 800;
  }
`;

const SectionTitle = styled.h1`
  margin: 0;
  font-size: 22px;
  font-weight: 600;
  letter-spacing: -0.01em;
`;

const Subtitle = styled.div`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 15px;
  margin-top: ${({ theme }) => theme.spacing(2)};
  max-width: 760px;
`;

const PHASES: readonly MtoModulePhase[] = [1, 2];

/**
 * Оболочка агента ОСНОВА.МТО. Постоянный левый рельс — навигация по
 * модулям (сгруппированы по фазам) + «Обзор» и «Все обработки». Основная область
 * показывает контекстный заголовок раздела и `<Outlet/>`. Активный пункт рельса и
 * заголовок выводятся из пути и `?module=` (модуль обработки живёт в URL — как у
 * Экономиста). На карточке обработки (`runs/:id`) заголовок раздела не дублируем —
 * его рисует сама карточка.
 */
export function MtoLayout() {
  const location = useLocation();
  const [params] = useSearchParams();
  const { can_read, can_write } = useAgentPermission('mto');

  const segments = location.pathname.split('/').filter(Boolean); // ['agents','mto',...]
  const section = segments[2];
  const hasId = Boolean(segments[3]);
  const isOverview = segments.length === 2;
  const onRuns = section === 'runs';
  const onQuality = section === 'quality';
  const onRequests = section === 'requests';
  const onSuppliers = section === 'suppliers';
  const onReceipts = section === 'receipts';
  const moduleParam = params.get('module');
  const activeModule = isMtoModuleId(moduleParam) ? moduleParam : null;
  const allRunsActive = onRuns && !hasId && !activeModule;
  const activeMeta = onRuns && !hasId && activeModule ? mtoModule(activeModule) : null;

  return (
    <Shell>
      <Rail aria-label="Навигация агента МТО">
        <Brand to="/agents/mto">
          ОСНОВА<span className="accent">.МТО</span>
        </Brand>
        <RailLink
          to="/agents/mto"
          $active={isOverview}
          aria-current={isOverview ? 'page' : undefined}
        >
          Обзор
        </RailLink>
        <RailLink
          to="/agents/mto/requests"
          $active={onRequests}
          aria-current={onRequests ? 'page' : undefined}
        >
          Входящие заявки
          <MtoRequestsRailCount />
        </RailLink>
        <RailLink
          to="/agents/mto/runs"
          $active={allRunsActive}
          aria-current={allRunsActive ? 'page' : undefined}
        >
          Все обработки
        </RailLink>
        <RailLink
          to="/agents/mto/quality"
          $active={onQuality}
          aria-current={onQuality ? 'page' : undefined}
        >
          Документы качества (АОСР)
        </RailLink>
        <RailLink
          to="/agents/mto/receipts"
          $active={onReceipts}
          aria-current={onReceipts ? 'page' : undefined}
        >
          Приход ТМЦ
        </RailLink>
        <RailLink
          to="/agents/mto/suppliers"
          $active={onSuppliers}
          aria-current={onSuppliers ? 'page' : undefined}
        >
          Поставщики
        </RailLink>
        {PHASES.map((phase) => (
          <Fragment key={phase}>
            <PhaseLabel>{MTO_PHASE_TITLES[phase]}</PhaseLabel>
            {MTO_MODULES.filter((m) => m.phase === phase).map((m) => {
              const active = onRuns && !hasId && activeModule === m.id;
              return (
                <RailLink
                  key={m.id}
                  to={`/agents/mto/runs?module=${m.id}`}
                  $active={active}
                  aria-current={active ? 'page' : undefined}
                  title={m.title}
                >
                  <RailNum>{m.number}</RailNum>
                  {m.title}
                </RailLink>
              );
            })}
          </Fragment>
        ))}
      </Rail>

      <Main>
        <Header>
          {isOverview ? (
            <>
              <Title>
                ОСНОВА<span className="accent">.МТО</span>
              </Title>
              <Subtitle>
                Снабжение объекта: считаем потребность в материалах по рабочей документации,
                сверяем со справочником материалов 1С, контролируем закупки и поставки, ведём
                паспорта качества.
              </Subtitle>
            </>
          ) : activeMeta ? (
            <>
              <SectionTitle>
                {activeMeta.number} · {activeMeta.title}
              </SectionTitle>
              <Subtitle>{activeMeta.description}</Subtitle>
            </>
          ) : allRunsActive ? (
            <>
              <SectionTitle>Все обработки</SectionTitle>
              <Subtitle>
                Все запущенные обработки агента по всем модулям. Откройте обработку, чтобы
                посмотреть результат, или выберите модуль слева, чтобы запустить новую.
              </Subtitle>
            </>
          ) : onRequests ? (
            <>
              <SectionTitle>Входящие заявки</SectionTitle>
              <Subtitle>
                Заявки в снабжение от инженеров ПТО и прорабов: что нужно на объект, по каким
                документам и к какому сроку. Примите заявку, возьмите её в работу и ответьте — автор
                увидит ответ в своей заявке и получит уведомление.
              </Subtitle>
            </>
          ) : onReceipts ? (
            <>
              <SectionTitle>Приход ТМЦ</SectionTitle>
            </>
          ) : onSuppliers ? (
            <>
              <SectionTitle>Поставщики</SectionTitle>
              <Subtitle>
                Реестр поставщиков: ИНН, контакты и что поставляют. Пополняется из загруженных КП и
                файлом реестра; по каждому поставщику видно, сколько от него КП и когда было
                последнее.
              </Subtitle>
            </>
          ) : onQuality ? (
            <>
              <SectionTitle>Документы качества (АОСР)</SectionTitle>
              <Subtitle>
                Прослеживаемость материалов для актов освидетельствования скрытых работ: реестр
                партий и их паспортов, сертификатов и деклараций, факты применения на объекте и две
                проверки — действителен ли документ на дату работ и нет ли применённых материалов
                без документов качества.
              </Subtitle>
            </>
          ) : null}
        </Header>
        {can_read && !can_write && <ReadOnlyBanner />}
        <Outlet />
      </Main>
    </Shell>
  );
}
